import React from 'react';
import {
  BloodworkAnalysis,
  FurtherConsiderations,
  PanelMarker,
  Section,
  RootCauseCategory,
} from '../../models';
import { AnemiaStatus } from './bloodwork-anemia-status';
import { ReferenceRangeAnalysisContainer } from '../../containers/bloodwork-ref-range-analysis-container';
import { WBCEvaluation } from './bloodwork-wbc-evaluation';
import { GlucoseRegulationInsights } from './glucose-regulation-insights';
import { NutrientInsights } from './nutrient-insights';
import { ThyroidRegulationInsights } from './thyroid-regulation-insights';
import { IronStatusInsights } from './iron-status-insights';
import { StressAdrenalRegulationInsights } from './stress-adrenal-regulation-insights';
import { DehydrationInsights, DEHYDRATION_PANEL_ENABLED } from './dehydration-insights';
import { LipidParticleInsights } from './lipid-particle-insights';
// insightsPanelPrintable is the whole of this file's interest in clinical
// review: a panel pending it is screen-only, so the notice that accompanies it
// on screen has nothing to sit above here.
import { insightsPanelPrintable } from './insights-review-notice';
import {
  insightsSourceAnalysis,
  previousCollectionLabel,
  PREVIOUS_COLLECTION_CAPTION_PDF_HEIGHT,
} from '../../helpers/previousOnlyMarkers';
import { sectionTextMarkers } from '../../helpers/customTextMarkers';
import {
  cardStartsItsOwnPage,
  sectionCardChunks,
} from '../../helpers/pdfSectionGeometry';
import {
  estimatedRbcInsightsPdfHeight,
  WBC_INSIGHTS_PDF_HEIGHT,
} from '../../helpers/pdfPanelMetrics';
import './previous-collection-insights.css';

type Props = {
  bloodworkAnalysis: BloodworkAnalysis;
  furtherConsiderations: FurtherConsiderations;
  title: string;
  section: Section;
  panelMarkers: PanelMarker[];
  /**
   * The practitioner's name for this section, when it is one of theirs. Every
   * such section prints as Section.CUSTOM_MARKERS, so without it the client's
   * PDF heads each one's results table with the same generic title.
   */
  practitionerSectionName?: string;
  /**
   * Suppress this section's text results on THIS chunk.
   *
   * Nutrient Status and Hormone Balance print as two PDFSections when
   * they run past 13 markers, both carrying the same `section`. Text markers
   * are resolved FROM the section, so without this both chunks would print the
   * whole set and the client's report would list every urinalysis line twice.
   * Set on the first chunk, so the words land under the last of the charts —
   * the reading order the single-page sections already have.
   */
  hideTextMarkers?: boolean;
  /**
   * Suppress this section's Insights panel on THIS chunk.
   *
   * Two callers, for two reasons:
   *
   *   - Nutrient Status prints as two PDFSections when it runs past 13
   *     markers, both carrying the same `section`, so without this the panel
   *     would print once above each half. Set on the SECOND chunk: the panel
   *     belongs with the first bars, the way it sits above them in every unsplit
   *     section.
   *   - the practitioner's "Include Insights Panel in PDF" checkbox (RBC,
   *     2026-09-22), which prints the bar graphs for the client and keeps the
   *     interpretation off their copy.
   *
   * Both want the same thing of this component — no panel, and the section title
   * back above the bars — so it stays one flag. What differs is only who sets it,
   * and the layout follows either way: hasEvaluationPanel reads this, so the
   * duplicate-title predictor and the panel/results page break both go with it.
   */
  hideInsightsPanel?: boolean;
  /**
   * Whether the page packer wants a page break AFTER this section.
   *
   * Emitted as this component's LAST element rather than as a class on the
   * wrapper the container puts around it. html2pdf's legacy pagebreak walks
   * `querySelectorAll('*')` — document order, so a parent is measured before
   * its children — and sizes each break's padding from the element's bottom
   * edge AT THAT MOMENT. A break on the outer wrapper was therefore measured
   * before the break INSIDE this section (the one that gives the results their
   * own page) had run, so its padding was short by most of a page and every
   * section that followed was displaced. Last-child ordering means both breaks
   * are measured after the inner one has taken effect.
   */
  breakAfter?: boolean;
};

/**
 * Whether `section` prints an Insights panel in this build, built from `ba`.
 *
 * The per-test question. INSIGHTS_PANEL_SECTIONS (insights-panel-sections.ts)
 * lists the sections this can answer yes for, for the two callers that need the
 * set without a test in hand.
 *
 * The single source of truth for that question, which two places have to agree
 * on: evaluation() renders the panel, and render() sizes the page around it
 * (the duplicate-title predictor and the keep-together wrapper). While they
 * were separate lists, render() carried an RBC/WBC/GLUCOSE allowlist that was
 * never widened as panels were added, so Nutrients, Thyroid, Stress &
 * Adrenal and Hydration each built a panel in evaluation() that render() never
 * asked for. None of the four reached the client's document.
 */
export function printsInsightsPanel(
  section: Section,
  bloodworkAnalysis: BloodworkAnalysis,
): boolean {
  // Fails closed: a panel still pending clinical review is held out of prod on
  // screen, and the PDF is the same content in the client's hands.
  if (!insightsPanelPrintable(section)) {
    return false;
  }
  switch (section) {
    // These two have printed whenever the section has an analysis to build from
    // since before the others existed, and they own their empty states:
    // AnemiaStatus always draws something, and Glucose is self-estimating, so
    // its height falls to zero on its own when the panel resolves to nothing.
    case Section.RBC:
    case Section.GLUCOSE:
      return true;
    // WBC does NOT own its empty state, which is why it is no longer grouped
    // with those two. WBCEvaluation renders null without a WBC value, while
    // this section's entry in EVALUATION_PANEL_HEIGHT is the last flat constant
    // in that table (300) and cannot fall to zero the way a self-estimating
    // panel's does. Answering "yes" for a panel that draws nothing therefore
    // reserves 300px of a page for it, and the section's results get pushed
    // onto a page of their own under a second copy of the title.
    //
    // Unreachable today: bloodwork-analysis-pdf.tsx only renders the Immune
    // section when a WBC value exists (showImmuneSection). But that is a
    // caller's guard standing in for this predicate's own correctness, and this
    // file's whole contract is that the render decision and the height are one
    // question asked once. Same question the screen asks in
    // BloodworkTestAnalysisSection.panelFor, and the same one render() answers.
    //
    // Asked of the analysis the panel will be BUILT from, so a section made
    // entirely of previous-only markers still prints that collection's gauge:
    // every caller passes the result of insightsSourceAnalysis.
    case Section.WBC:
      return !!bloodworkAnalysis?.wbc?.hasValue?.();
    case Section.NUTRIENTS:
      return NutrientInsights.shouldRender(bloodworkAnalysis);
    case Section.THYROID:
      return ThyroidRegulationInsights.shouldRender(bloodworkAnalysis);
    case Section.IRON:
      return IronStatusInsights.shouldRender(bloodworkAnalysis);
    case Section.STRESS_ADRENAL:
      return StressAdrenalRegulationInsights.shouldRender(bloodworkAnalysis);
    case Section.ELECTROLYTES:
      return (
        DEHYDRATION_PANEL_ENABLED &&
        DehydrationInsights.shouldRender(bloodworkAnalysis)
      );
    /*
     * Lipid Particle Insights. Unreachable today — `insightsPanelPrintable`
     * returns false above because LIPIDS is in SECTIONS_PENDING_CLINICAL_REVIEW
     * — and written anyway, for the reason the WBC comment above gives: the
     * `default` arm would answer "no" the day this section leaves that list,
     * and a panel that silently stops at the PDF boundary after shipping on
     * screen is the hard version of this bug to notice. Same predicate as the
     * screen asks in BloodworkTestAnalysisSection.panelFor.
     */
    case Section.LIPIDS:
      return LipidParticleInsights.shouldRender(bloodworkAnalysis);
    default:
      return false;
  }
}

export class PDFSection extends React.Component<Props, {}> {
  furtherConsiderationsFor(
    rootCauseCategory: RootCauseCategory,
    section: string,
    tab: string,
  ): JSX.Element {
    const { furtherConsiderations } = this.props;
    return (
      <span key={rootCauseCategory}>
        {
          // tslint:disable-next-line:max-line-length
          furtherConsiderations
            .findConsiderations(rootCauseCategory, section, tab)
            .map((consideration, cindex) => {
              return (
                <span key={cindex}>
                  <span key={`title-${cindex}`} className="consideration-title">
                    {consideration.title}
                  </span>
                  <ul>
                    {consideration.items.map((item, itemIndex) => {
                      return (
                        <li key={`${rootCauseCategory}-${cindex}-${itemIndex}`}>
                          {item}
                        </li>
                      );
                    })}
                  </ul>
                </span>
              );
            })
        }
      </span>
    );
  }

/*
  preparedFor(bloodworkAnalysis: BloodworkAnalysis) {
    return (
      <div>
        <h6 className="prepared-for-container">
          <strong>Prepared for: </strong>
          <span>
            {bloodworkAnalysis.bloodwork.client.firstName}{' '}
            {bloodworkAnalysis.bloodwork.client.lastName}
          </span>
          <strong style={{ marginLeft: 20 }}>Collection Date: </strong>
          <span>
            <Moment format="MM/DD/YYYY">
              {bloodworkAnalysis.bloodwork.collectionDate}
            </Moment>
          </span>
        </h6>
      </div>
    );
  }
*/

  /**
   * The section's Insights panel, built from `bloodworkAnalysis`.
   *
   * That is the CURRENT analysis, except in a section made entirely of
   * previous-only markers, where it is the previous one and `fromPrevious`
   * captions the panel with that collection's date. Same rule as the on-screen
   * section (BloodworkTestAnalysisSection), because this document is the
   * client's copy of that screen.
   */
  evaluation(
    title: string,
    section: string,
    bloodworkAnalysis: BloodworkAnalysis,
    fromPrevious: boolean,
  ) {
    // Whether it prints at all is printsInsightsPanel's call, asked once so this
    // and the page-sizing in render() can never disagree. What is left here is
    // only WHICH component to build.
    if (!printsInsightsPanel(section as Section, bloodworkAnalysis)) {
      return null;
    }
    // RBC, WBC, and GLUCOSE all render an upper "Insights" panel followed by
    // their bar graphs. We rely on the per-section JSX in
    // bloodwork-analysis-pdf.tsx to push these sections onto a fresh page
    // (via the surrounding `<div className="html2pdf__page-break" />`), so we
    // do NOT add another empty break div here — adding one when the prior
    // section already forced a break results in a blank page slipping in.
    const panel =
      section === Section.RBC ? (
        <AnemiaStatus bloodworkAnalysis={bloodworkAnalysis} />
      ) : section === Section.WBC ? (
        <WBCEvaluation bloodworkAnalysis={bloodworkAnalysis} />
      ) : section === Section.GLUCOSE ? (
        <GlucoseRegulationInsights bloodworkAnalysis={bloodworkAnalysis} />
      ) : section === Section.NUTRIENTS ? (
        // The Open Questions this panel prints are client-facing by decision:
        // they name the follow-up the practitioner would order, and the client
        // seeing them is what supports that conversation.
        <NutrientInsights bloodworkAnalysis={bloodworkAnalysis} />
      ) : section === Section.THYROID ? (
        <ThyroidRegulationInsights bloodworkAnalysis={bloodworkAnalysis} />
      ) : section === Section.IRON ? (
        // Wired on the day the panel was built (2026-09-20) so it is never
        // screen-only; printsInsightsPanel holds it out of every PDF while it
        // is pending clinical review.
        <IronStatusInsights bloodworkAnalysis={bloodworkAnalysis} />
      ) : section === Section.STRESS_ADRENAL ? (
        // Added 2026-08-21 so the panel is not screen-only. The PDF is the
        // client's copy of the screen, so an insights panel the practitioner
        // sees on screen has to reach the document too — the env gate on the
        // screen side already decides whether it renders at all.
        <StressAdrenalRegulationInsights bloodworkAnalysis={bloodworkAnalysis} />
      ) : section === Section.ELECTROLYTES ? (
        <DehydrationInsights bloodworkAnalysis={bloodworkAnalysis} />
      ) : null;
    if (!panel) {
      return null;
    }
    return (
      <div>
        <h4 className="pdf-section-title"><strong>{title}</strong></h4>
        {this.furtherConsiderationsFor(
          RootCauseCategory.PDF,
          section,
          'Evaluation',
        )}
        <div className="pdf-anemia-status">
          {fromPrevious ? (
            <div className="previous-collection-insights">
              <div className="previous-collection-caption">
                {previousCollectionLabel(bloodworkAnalysis.bloodwork)}
              </div>
              {panel}
            </div>
          ) : (
            panel
          )}
        </div>
      </div>
    );
  }

  refRangeTitle(): string {
    const { section } = this.props;
    if (section === Section.WBC) {
      return 'Immune Activity Results';
    } else if (section === Section.PLATELETS) {
      return 'Blood Clotting & Flow Results';
    }
    else if (section === Section.PROTEINS) {
      return 'Protein Results';
    }
    else if (section === Section.NUTRIENTS) {
      return 'Nutrient Results';
    }
    else if (section === Section.SEX_HORMONE) {
      return 'Hormone Results';
    }
    else if (section === Section.INFLAMMATION_OXIDATIVE_STRESS) {
      return 'Inflammation & Oxidative Stress Results';
    }
    else if (section === Section.LIPIDS) {
      return 'Lipid Results';
    }
    else if (section === Section.LIVER_BILIARY) {
      return 'Liver Results';
    }
    else if (section === Section.ELECTROLYTES) {
      return 'Electrolyte Results';
    }
    else if (section === Section.GLUCOSE) {
      return 'Glucose Regulation & Metabolic Function Results';
    }
    else if (section === Section.CUSTOM_MARKERS) {
      // A section the practitioner named is titled by that name — see the same
      // branch in tabs-component.
      if (this.props.practitionerSectionName) {
        return `${this.props.practitionerSectionName} Results`;
      }
      // The default would read "Other Lab Results Results".
      return Section.CUSTOM_MARKERS;
    }
    return `${section} Results`;
  }

  render() {
    const { bloodworkAnalysis, panelMarkers, title, section } = this.props;
    // Resolved here rather than passed in, for the same reason the screen
    // resolves it in tabs-component: a section is identified by the same two
    // values in both places, and threading a third marker list down through
    // every call site in bloodwork-analysis-pdf.tsx would be eighteen chances
    // to pass the wrong one.
    const allTextMarkers = this.props.hideTextMarkers
      ? []
      : sectionTextMarkers(
          bloodworkAnalysis,
          section,
          this.props.practitionerSectionName,
        );
    // WHOLE, always. A section longer than a page is divided below, into cards
    // this component renders and prints the breaks between; the container used
    // to cut the rows itself and hand this a window of them, for its
    // practitioner sections only, which is why every other section ran off the
    // page instead.
    const textMarkers = allTextMarkers;
    // A section can be entirely text — a urinalysis panel is exactly that — so
    // an empty bar-graph list is no longer an empty section.
    if (panelMarkers.length === 0 && textMarkers.length === 0) {
      return null;
    }
    // Per-section notes are no longer rendered here — they are consolidated into
    // the Section Notes panel near the report bottom (SectionNotesSummaryPanel),
    // so a long note no longer competes with this section's bar graphs for space
    // on the page.

    // The practitioner's own markers have no bibliography anchor to point at.
    const infoLink =
      section === Section.CUSTOM_MARKERS
        ? undefined
        : `https://labsmarts.com/bibliography/#${section}`;
    // Sections that render their own panel + title in evaluation() already
    // have the section title at the top of the page. The bar-graphs wrapper
    // below uses page-break-inside: avoid (via .pdf-glucose-bar-graphs) so
    // when the bar-graphs don't fit on the Insights page they move whole
    // to the next page. In that case we want the title shown above them on
    // the new page so the reader knows what they are looking at; when they
    // DO fit on the Insights page, the title would be a duplicate and we
    // hide it.
    //
    // A section built entirely from previous-only markers (values last test,
    // none this one) prints the PREVIOUS collection's panel, captioned with
    // that date — never a blank gauge built from the current test. With no
    // previous analysis to fall back on there is no panel at all, and the
    // title prints above the bar graphs like any other panel-less section.
    const insightsAnalysis = insightsSourceAnalysis(
      section,
      panelMarkers,
      bloodworkAnalysis,
    );
    const insightsFromPrevious =
      !!insightsAnalysis && insightsAnalysis !== bloodworkAnalysis;
    const hasEvaluationPanel =
      !!insightsAnalysis &&
      !this.props.hideInsightsPanel &&
      printsInsightsPanel(section, insightsAnalysis);
    // Layout estimates mirror the constants used by the PDF packer. We use
    // them to predict whether the bar-graphs wrapper will fit on the same
    // page as its Insights panel; if not, we keep the title.
    /*
     * The fit test itself lives in pdfPanelMetrics now — see
     * barGraphsShareInsightsPage — because the section packer in
     * bloodwork-analysis-pdf.tsx has to know the same answer, and asking it
     * separately is what let the two disagree about a printed page.
     */
    // The card's own measurements are not made here any more: sectionCardChunks
    // takes the markers and the text results and returns the cards they print
    // as, each with the height the packer next door will budget it at.
    // The RBC Insights panel (AnemiaStatus) is not a fixed height — it grows
    // with each conditional finding it renders (nutrient-deficiency note, bone-
    // marrow overproduction, possible root-cause list, dehydration, poor
    // oxygenation, smoking). A single fixed estimate under-counts a panel full
    // of findings, so the predictor thinks the bar graphs fit beneath it and
    // hides the title — even though html2pdf actually pushes the Results onto a
    // fresh page, leaving them with no title. Estimate RBC from the same
    // signals AnemiaStatus uses so the prediction tracks the rendered height.
    //
    // Reads the analysis the panel is actually BUILT from, which for a
    // previous-only section is the previous one: estimating a printed panel
    // from a different test's findings is how the title-duplication predictor
    // goes wrong.
    const panelAnalysis = insightsAnalysis ?? bloodworkAnalysis;
    const EVALUATION_PANEL_HEIGHT: { [section: string]: number } = {
      // Both live in pdfPanelMetrics, because the section packer needs them
      // too: a section it cannot cost is one whose page breaks have to be
      // hardcoded in the JSX.
      [Section.RBC]: estimatedRbcInsightsPdfHeight(panelAnalysis),
      [Section.WBC]: WBC_INSIGHTS_PDF_HEIGHT,
      // GlucoseRegulationInsights grows with every add-on pattern box it
      // resolves, so it estimates its own height (see
      // GlucoseRegulationInsights.estimatedPdfHeight) rather than carrying a
      // fixed number here. A standard single-pattern panel measures 280 — the
      // value this predictor was previously tuned to, which keeps the common
      // case unchanged: the full 8-base-marker glucose panel (glucose, HbA1c,
      // insulin, C-peptide, TG/HDL, TG, HDL, amylase) still fits under the
      // gauge with margin (280+24+516+60 = 880 <= 900) and the duplicate title
      // stays hidden. The same estimate feeds the PDF section packer, so the
      // two views of this panel's height can't drift apart.
      [Section.GLUCOSE]: GlucoseRegulationInsights.estimatedPdfHeight(panelAnalysis),
      // Same self-estimating approach as Glucose: this panel's height varies
      // with how many patterns and open questions resolve, so a fixed number
      // would mis-paginate every sparse panel (which is most of them).
      [Section.NUTRIENTS]: NutrientInsights.estimatedPdfHeight(panelAnalysis),
      // Both self-estimate for the same reason: they grow with the number of
      // pattern boxes / driver markers that resolve.
      [Section.THYROID]: ThyroidRegulationInsights.estimatedPdfHeight(panelAnalysis),
      [Section.IRON]: IronStatusInsights.estimatedPdfHeight(panelAnalysis),
      [Section.STRESS_ADRENAL]:
        StressAdrenalRegulationInsights.estimatedPdfHeight(panelAnalysis),
      [Section.ELECTROLYTES]: DEHYDRATION_PANEL_ENABLED
        ? DehydrationInsights.estimatedPdfHeight(panelAnalysis)
        : 0,
      // Self-estimating like the rest. Phase 1 is two fixed SVG boxes, so the
      // only thing that varies is whether the LP-IR strip is present; it will
      // start varying properly once the area bars and pattern boxes land.
      [Section.LIPIDS]: LipidParticleInsights.estimatedPdfHeight(panelAnalysis),
    };
    /*
     * The section's results, cut into pages.
     *
     * A section taller than a page cannot be printed as one card: html2pdf
     * carries the overflow onto the next page with no heading and no card
     * header over it, and a reader meeting a column of results at the top of a
     * page has nothing telling them what they are. Thirty-four urinalysis
     * results do that; so does any section a practitioner has filed a long list
     * of text results into.
     *
     * Cut HERE rather than in the container, so it happens for every section
     * that renders through this component rather than for the one the bug was
     * reported against. When the container has already cut a section into
     * A section that fits on one page finds exactly one card here and prints
     * precisely as it always did.
     */
    const cardChunks = sectionCardChunks(panelMarkers.length, textMarkers);
    // Only ever read through hasEvaluationPanel, which has already settled
    // whether this build prints the panel (printsInsightsPanel), so a section
    // held back for clinical review never reaches this line.
    const evaluationPanelHeight = EVALUATION_PANEL_HEIGHT[section] ?? 0;
    // Zero means the panel renders nothing (an unresolved glucose gauge, say),
    // and a caption for a panel that isn't there costs no height.
    //
    // No "pending clinical review" notice is costed here any more: a panel
    // pending review is screen-only now, so a printing panel is by definition
    // a reviewed one and the notice never reaches the page.
    const evaluationHeight =
      evaluationPanelHeight > 0 && insightsFromPrevious
        ? evaluationPanelHeight + PREVIOUS_COLLECTION_CAPTION_PDF_HEIGHT
        : evaluationPanelHeight;
    // Asked of the first CARD, not of the whole section: a section long enough
    // to be cut fills that card to the page, so it separates from its panel —
    // and a section that fits on one card asks exactly what it always asked.
    const barGraphsFitOnEvaluationPage =
      hasEvaluationPanel &&
      !cardStartsItsOwnPage(evaluationHeight, cardChunks);
    /*
     * ONE decision, used twice: the results either share the Insights page or
     * take the next one, and the heading follows that choice.
     *
     * They were two decisions before, and they disagreed. The title was shown
     * whenever the fit test said no, but nothing then made the break happen —
     * the wrapper's page-break-inside was the only mechanism, and this file's
     * own notes explain why that is unreliable. So the results printed a second
     * heading and then began under it on the SAME page, spilling the rest onto
     * the next: a duplicate heading above Stress & Adrenal's results, and
     * the Nutrient Status bars split across two pages.
     *
     * An explicit break is deterministic where page-break-inside is not.
     */
    const barGraphsOnOwnPage = hasEvaluationPanel && !barGraphsFitOnEvaluationPage;
    const showBarGraphsTitle = !hasEvaluationPanel || barGraphsOnOwnPage;
    // Apply page-break-inside: avoid to sections whose bar graphs would
    // otherwise be split mid-table by html2pdf. RBC/WBC/GLUCOSE need it
    // because their bar-graphs wrapper must stay together with (or move
    // whole below) their Insights panel above. INFLAMMATION can have up
    // to 13 markers and gets close to the page-capacity ceiling — without
    // keep-together html2pdf tends to leave the title at the bottom of
    // the prior page and overflow the bar graphs onto the next one.
    //
    // The SET IS FIXED, and deliberately not "every section with a panel".
    // Widening it was an unintended side effect of teaching hasEvaluationPanel
    // about the newer panels: page-break-inside is the rule this file's own
    // notes call unreliable (it silently no-ops once the page-grid drift
    // exceeds a line, and it strands the rest of a page when it does fire), so
    // four more sections quietly acquired it and started leaving half-blank
    // pages. These four earned it by measurement over many exports; a new
    // section joins them the same way, not by having a panel.
    const KEEP_TOGETHER_SECTIONS: Section[] = [
      Section.RBC,
      Section.WBC,
      Section.GLUCOSE,
      Section.INFLAMMATION_OXIDATIVE_STRESS,
    ];
    const needsKeepTogether = KEEP_TOGETHER_SECTIONS.includes(section);
    const sectionWrapperClassName = needsKeepTogether ? 'pdf-glucose-bar-graphs' : '';
    return (
      <>
        {hasEvaluationPanel &&
          this.evaluation(title, section, panelAnalysis, insightsFromPrevious)}
        {barGraphsOnOwnPage && <div className="html2pdf__page-break" />}
        {cardChunks.map(chunk => (
          <React.Fragment key={chunk.index}>
            {/* Each card after the first is reached by a break of its own, so
                it opens a page with its heading at the top of it rather than
                continuing under whatever the last one ended with. */}
            {chunk.isCont && <div className="html2pdf__page-break" />}
            <div className={sectionWrapperClassName}>
              {(showBarGraphsTitle || chunk.isCont) && (
                <h4 className="pdf-section-title">
                  <strong>
                    {/* Nutrient Status and Hormone Balance arrive here
                        already titled "(cont.)" — they split their charts in the
                        container, each part a PDFSection of its own — so a
                        second card of one of those parts must not say it
                        twice. */}
                    {chunk.isCont && !/\(cont\.\)\s*$/.test(title)
                      ? `${title} (cont.)`
                      : title}
                  </strong>
                </h4>
              )}
              {/* The section's considerations belong to the section, not to a
                  page of it: repeating them over every continuation is how one
                  paragraph becomes four. */}
              {!chunk.isCont &&
                this.furtherConsiderationsFor(
                  RootCauseCategory.PDF,
                  section,
                  'Analysis',
                )}
              <div className="pdf-ref-range-analysis">
                <ReferenceRangeAnalysisContainer
                  title={this.refRangeTitle()}
                  section={section}
                  bloodwork={bloodworkAnalysis.bloodwork}
                  previousBloodwork={bloodworkAnalysis.previousBloodwork}
                  infoLink={infoLink}
                  panelMarkers={panelMarkers.slice(
                    chunk.markerStart,
                    chunk.markerEnd,
                  )}
                  textMarkers={textMarkers.slice(chunk.textStart, chunk.textEnd)}
                  forPdf
                />
              </div>
            </div>
          </React.Fragment>
        ))}
        {/* LAST, and it has to stay last: html2pdf measures each break in
            document order, so this one must be reached after the panel/results
            break above has already pushed the results onto their own page. */}
        {this.props.breakAfter && <div className="html2pdf__page-break" />}
        {/* <PDFTab
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Insights"
                    section={section}
                    tab="Interpretation"
                />                
                <PDFTab
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Additional Testing"
                    section={section}
                    tab="Testing"
                />
                <PDFTab 
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Further Considerations"
                    section={section}
                    tab="Considerations"
                />
                <PDFTab 
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Signs and Symptoms"
                    section={section}
                    tab="Symptoms"
                />
                <PDFTab 
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Diet"
                    section={section}
                    tab="Diet"
                />
                <PDFTab 
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Lifestyle"
                    section={section}
                    tab="Lifestyle"
                />
                <PDFTab 
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Supplements"
                    section={section}
                    tab="Supplements"
                />
                */}
      </>
    );
  }
}
